import React, { useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction, ReactNode } from "react";
import { Col, DatePicker, Form, Input, Modal, Row, Select, Table, Tooltip } from "antd";
import type { ModalProps } from "antd";
import moment from "moment";
import type { Moment } from "moment";
import { DataNode } from "antd/lib/tree";
import { useAppSelector } from "../../..";
import { ReactComponent as TransitionIcon } from "../../../img/transition_icon.svg";
import type { TableColumn } from "../../../lib/interfaces";
import type { Agreement, Offer, IChangingRecordKey } from "../../../lib/types";
import {
  checkIsLoading,
  displayFormat,
  getSelectOptions,
  toDisplayFormat,
  useActiveModal,
  onlyNumbers,
} from "../../../lib/utils";
import { ProductParamsTree } from "../ProductParamsTree/ProductParamsTree";
import ToggleColumns from "../../ToggleableVersionsTable/ToggleColumns";
import type { IOfferHierarchy } from "../../../store/slices/offering/types";
import type {
  IOfferParamsTableRecord,
  RequestParams,
} from "../../../containers/AccountProductsContainer/ProductCreateContainer/utils/types";
import { hasAccess } from "../../RolesModule/hasAccess";
import { PROLONGATION_WRITE, SERVICE_CHANGE_CHILDSTATUSES } from "../../../lib/const/permissions";
import { OfferList } from "../../OfferList/OfferList";
import { useOfferList } from "../../OfferList/utils/useOfferList";
import { ReactComponent as LoupeIcon } from "../../../img/loupe-icon.svg";
import { PRODUCT_STATUSES } from "../../../store/slices/products/const/productStatuses";
import { IOfferListSelectMode } from "../../../containers/OfferContainer/context/types";
import "./ProductParams.less";

const columnsWidths: Partial<Record<keyof IOfferParamsTableRecord | "btns", string>> = {
  id: "50px",
  extName: "",
  name: "",
  serviceStatusId: "",
  prolongTypeId: "120px",
  prolongOffer: "150px",
  fromDate: "100px",
  toDate: "100px",
  btns: "100px",
  quantity: "100px",
};

interface ComponentProps {
  treeExpandedKeys: string[];
  setTreeExpandedKeys: Dispatch<SetStateAction<string[]>>;
  offer: Offer<string> | undefined;
  offerHierarchy: IOfferHierarchy | undefined;
  tableData: IOfferParamsTableRecord[];
  onChangeTableData: (props: {
    fieldName: keyof IOfferParamsTableRecord;
    key: string;
    value: any;
    changeMode?: "single" | "children" | "all" | undefined;
  }) => void;
  onChangeTableOfferData: (props: {
    fieldName: keyof Offer<string>;
    fieldNameTableRow: keyof IOfferParamsTableRecord;
    key: string;
    value: any;
    changeMode?: "single" | "children" | "all" | undefined;
  }) => void;
  agreement: Agreement;
  openOfferDetailsModal: (key: string) => void;
  addedOfferProductsParams?: IOfferHierarchy | Offer;
  setAddedOfferProductsParams: React.Dispatch<
    React.SetStateAction<IOfferHierarchy | Offer | undefined>
  >;
  setChangingRecordKey: React.Dispatch<React.SetStateAction<IChangingRecordKey | undefined>>;
  treeDataFormat: DataNode[];
  setTreeDataFormat: React.Dispatch<React.SetStateAction<DataNode[]>>;
  offerListSelectMode: IOfferListSelectMode;
  getCurrentOfferHierarchy: (params: RequestParams) => void;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ProductParams = (props: ComponentProps) => {
  const {
    treeExpandedKeys,
    setTreeExpandedKeys,
    offer,
    offerHierarchy,
    tableData,
    onChangeTableData,
    onChangeTableOfferData,
    agreement,
    openOfferDetailsModal,
    treeDataFormat,
    setTreeDataFormat,
    offerListSelectMode,
    getCurrentOfferHierarchy,
  } = props;

  const serviceStatus = useAppSelector((state) => state.rtkDictionaries.serviceStatus);
  const { serviceStatusLoading } = useAppSelector((state) => state.rtkDictionaries);

  const offeringProlongTypes = useAppSelector((state) => state.dicts.offeringProlongTypes);

  const { offeringProlongTypesLoading } = useAppSelector((state) => state.dicts);

  const { hierarchyOfferList } = useAppSelector((state) => state.offering);
  // console.log({ hierarchyOfferList });

  const { activeModal, setActiveModal, activeModalOptions } = useActiveModal<
    "" | "selectOfferForProlongation"
  >("");

  const prolongOfferListProps = useOfferList({
    initialOfferListViewMode: "selectOffer",
    initialSearchValues: {
      isStandalone: true,
      onDate: moment(),
      offeringStatusId: 2,
      page: 0,
      size: 20,
    },
  });

  // console.log({ prolongOfferListProps });

  // Синхронизация отображения строк таблицы с деревом
  const displayedTableData = useMemo(
    () =>
      tableData.filter(({ parentId }) =>
        parentId ? treeExpandedKeys.includes(String(parentId)) : true
      ),
    [tableData, treeExpandedKeys]
  );

  const getProlongOfferTitle = (prolongOffer: Partial<Offer> | undefined): ReactNode | "" => {
    if (prolongOffer) {
      const { extName, name, id } = prolongOffer;
      return [extName, name, id].map((item, idx) => <span key={idx}>{item}</span>);
    }
    return "";
  };

  const onChangeFromDate = (value: Moment | null, key: string, prevValue?: Moment | null) => {
    if (offerListSelectMode !== "select") {
      onChangeTableOfferData({
        key,
        fieldName: "fromDate",
        fieldNameTableRow: "fromDate",
        value,
        changeMode: "all",
      });

      onChangeTableData({
        key,
        fieldName: "fromDate",
        value,
        changeMode: "all",
      });
    } else {
      const onConfirm = () => {
        if (value) {
          const params = {
            onDateValue: value,
          };

          getCurrentOfferHierarchy && getCurrentOfferHierarchy(params);
          onChangeTableData({
            key,
            fieldName: "fromDate",
            value,
            changeMode: "all",
          });
        }
      };

      const onCancel = () => {
        if (prevValue) {
          onChangeTableData({
            key,
            fieldName: "fromDate",
            value,
            changeMode: "all",
          });
        }
      };

      Modal.confirm({
        content:
          "Параметры Предложения могут быть изменены. Вы хотите обновить данные о Предложении?",
        okText: "Да",
        okButtonProps: { style: { width: "108px" } },
        className: "offer-custom-modal-for-params-change",
        onOk: onConfirm,
        onCancel,
        cancelText: "Нет",
        cancelButtonProps: { style: { width: "108px" } },
      });
    }
  };

  const onChangeToDate = (value: Moment | null, key: string) => {
    onChangeTableData({
      key,
      fieldName: "toDate",
      value,
    });
    // toDate вложенного оффера и его сущностей, всегда бесконечность - поэтому не меняем
    // onChangeTableOfferData({
    //   key,
    //   fieldName: "toDate",
    //   value,
    // });
  };

  const getGroupOfferColumns = (): TableColumn<IOfferParamsTableRecord>[] => {
    return [
      {
        title: "ID предложения",
        key: "id",
        dataIndex: "id",
        width: columnsWidths.id,
        hidden: true,
      },
      // {
      //   title: "Наименование",
      //   key: "extName",
      //   dataIndex: "extName",
      //   width: columnsWidths.extName,
      //   render: (value) => {
      //     return (
      //       <Tooltip title={value}>
      //         <span className="ant-table-cell-short">{value}</span>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        title: "Код",
        key: "name",
        dataIndex: "name",
        width: columnsWidths.name,
        render: (value) => {
          return (
            <Tooltip title={value}>
              <div
                style={{
                  maxWidth: "100px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <span>{value}</span>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "Статус",
        key: "serviceStatusId",
        dataIndex: "serviceStatusId",
        width: columnsWidths.serviceStatusId,
        render: (v, { key, parentId }) => {
          const serviceStatusIdRoot = tableData[0]?.serviceStatusId;

          const filteredServiceStatuses = serviceStatus.filter(
            ({ id }) => ![PRODUCT_STATUSES.CANCELLED].includes(id as any)
          );

          const getFilteredServiceStatuses = (serviceStatusIdRoot?: number) => {
            // Если корневой продукт
            if (!parentId) {
              return filteredServiceStatuses;
            }

            // Если доступно изменение статусов дочерних продуктов на любые
            if (hasAccess(SERVICE_CHANGE_CHILDSTATUSES)) {
              return filteredServiceStatuses;
            }

            switch (serviceStatusIdRoot) {
              case PRODUCT_STATUSES.PENDING:
              case PRODUCT_STATUSES.BLOCKED:
              case PRODUCT_STATUSES.ACTIVE:
                return filteredServiceStatuses.filter(({ id }) =>
                  [PRODUCT_STATUSES.PROVIDED, v, serviceStatusIdRoot].includes(id as any)
                );
              default:
                return filteredServiceStatuses;
            }
          };

          // Если статус корневого продукта Оказан и bundleTypeId = 3 (Целиком), то статус дочерних продуктов дизейблится
          const isDisabled =
            serviceStatusIdRoot === PRODUCT_STATUSES.PROVIDED &&
            tableData[0]?.offering?.bundleTypeId === 3 &&
            Boolean(parentId);

          return (
            <Form.Item
              name={`${key}-serviceStatusId`}
              rules={[
                {
                  required: true,
                },
              ]}
              className="form-item-required__hidden-error__mb0"
            >
              <Select
                disabled={isDisabled}
                options={getSelectOptions({
                  list: getFilteredServiceStatuses(serviceStatusIdRoot),
                })}
                loading={checkIsLoading(serviceStatusLoading)}
                style={{ width: "110px" }}
                onChange={(value) => {
                  console.log(`${key}-serviceStatusId`);
                  onChangeTableData({
                    key,
                    fieldName: "serviceStatusId",
                    value,
                    changeMode: "children",
                  });
                }}
              />
            </Form.Item>
          );
        },
      },
      {
        title: "Пролонгация",
        key: "prolongTypeId",
        dataIndex: "prolongTypeId",
        width: columnsWidths.prolongTypeId,
        render: (v, { key }) => {
          // const isEditable =
          //   offering.prolongTypeId === 2 ||
          //   (offering.prolongTypeId === 1 && hasAccess(PROLONGATION_WRITE));
          const isEditable = hasAccess(PROLONGATION_WRITE);
          const onChange = (value) => {
            onChangeTableData({ key, fieldName: "prolongTypeId", value });
            if (value === 1) {
              prolongOfferListProps.setSelectedOffer(undefined);
              // Если предложение для пролонгации было выбрано ранее, то по выбору "без пролонгации", его сброс
              onChangeTableData({ key, fieldName: "prolongOffer", value: undefined });
            }
          };
          return (
            <Form.Item
              name={`${key}-prolongTypeId`}
              className="form-item-required__hidden-error__mb0"
            >
              <Select
                options={getSelectOptions({
                  list: offeringProlongTypes,
                })}
                loading={checkIsLoading(offeringProlongTypesLoading)}
                style={{ width: "110px" }}
                onChange={onChange}
                disabled={!isEditable}
                allowClear
              />
            </Form.Item>
          );
        },
        hidden: true,
      },
      {
        title: "Количество",
        key: "quantity",
        dataIndex: "quantity",
        width: columnsWidths.quantity,
        hidden: false,
        render: (v, { key }) => {
          return (
            <Form.Item
              name={`${key}-quantity`}
              className="form-item-required__hidden-error__mb0"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input
                onInput={(e) => {
                  onlyNumbers(e, { withoutZero: true });
                }}
                onChange={({ target }) => {
                  const valueNum = Number(target.value);
                  onChangeTableData({
                    key,
                    fieldName: "quantity",
                    value: valueNum || undefined,
                  });
                }}
              />
            </Form.Item>
          );
        },
      },
      {
        title: "Предложение для пролонгации",
        key: "prolongOffer",
        dataIndex: "prolongOffer",
        width: columnsWidths.prolongOffer,
        render: (v, { key, prolongTypeId, prolongOffer, offering }) => {
          const isEditable =
            prolongTypeId === 2 || (!prolongTypeId && offering.prolongTypeId === 2);
          const openSearchModal = () => {
            setActiveModal("selectOfferForProlongation", { key });
            prolongOfferListProps.setSelectedOffer(undefined);
          };
          // const formFieldName = `${key}-prolongOffer`;
          // validateFields([formFieldName]);
          return (
            <Input
              onClick={openSearchModal}
              disabled={!isEditable}
              value=""
              placeholder={prolongOffer || !isEditable ? "" : "Выберите предложение"}
              prefix={
                isEditable && (
                  <div onClick={openSearchModal} className="product-params__prolongOffer">
                    <Tooltip title="Расширенный поиск предложения">
                      <LoupeIcon style={{ cursor: "pointer", margin: "-4px 7px -3px -3px" }} />
                    </Tooltip>
                    <Tooltip
                      title={getProlongOfferTitle(prolongOffer)}
                      overlayClassName="product-params__prolongOfferTooltip"
                    >
                      {prolongOffer?.name}
                    </Tooltip>
                  </div>
                )
              }
            />
          );
        },
        hidden: true,
      },
      {
        title: "Дата активации",
        key: "fromDate",
        dataIndex: "fromDate",
        width: columnsWidths.fromDate,
        render: (v, { fromDate, key, offering }, idx) => {
          if (idx === 0) {
            const disableFromDate = (date: Moment) => {
              const invalidByOffering =
                date.isBefore(offering.fromDate) || date.isAfter(offering.toDate);
              const invalidByAgreement =
                date.isBefore(agreement.fromDate) || date.isAfter(agreement.toDate);
              if (offerListSelectMode === "select") {
                return invalidByOffering || invalidByAgreement;
              }
              return invalidByAgreement;
            };
            return (
              <Form.Item
                name={`${key}-fromDate`}
                style={{ width: "117px" }}
                className="form-item-required__hidden-error__mb0"
                rules={[{ required: true }]}
                // initialValue={fromDate}
              >
                <DatePicker
                  format={displayFormat}
                  disabledDate={disableFromDate}
                  onChange={(value) => {
                    onChangeFromDate(value, key, fromDate);
                  }}
                  allowClear={false}
                />
              </Form.Item>
            );
          }
          return <div style={{ paddingLeft: "13px" }}>{toDisplayFormat(fromDate)}</div>;
        },
      },
      {
        title: "Дата окончания",
        key: "toDate",
        dataIndex: "toDate",
        width: columnsWidths.toDate,
        render: (v, { fromDate, key }) => {
          const disableToDate = (date: Moment) =>
            date.isBefore(fromDate) ||
            date.isBefore(agreement.fromDate) ||
            date.isAfter(agreement.toDate);
          return (
            <Form.Item
              name={`${key}-toDate`}
              style={{ width: "117px" }}
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true }]}
              // initialValue={toDate}
            >
              <DatePicker
                format={displayFormat}
                disabledDate={disableToDate}
                onChange={(value) => {
                  onChangeToDate(value, key);
                }}
              />
            </Form.Item>
          );
        },
      },
      {
        title: "Предложение",
        key: "btns",
        dataIndex: "btns",
        width: columnsWidths.btns,
        render: (v, { key }) => {
          return (
            <Row justify="center" style={{ marginTop: "-10px" }}>
              <Tooltip title="Параметры предложения">
                <TransitionIcon
                  className="product-params__transitionBtn"
                  onClick={() => {
                    openOfferDetailsModal(key);
                  }}
                />
              </Tooltip>
            </Row>
          );
        },
      },
    ];
  };

  const [columns, setColumns] = useState<TableColumn<IOfferParamsTableRecord>[]>(
    getGroupOfferColumns()
  );

  // Актуализация колонок при изменении справочников с сохранением скрытости по тогглеру
  useEffect(() => {
    const updatedColumns = getGroupOfferColumns();
    setColumns((prev) =>
      updatedColumns.map((column) => {
        const prevColumn = prev.find(({ dataIndex }) => dataIndex === column.dataIndex);
        return { ...column, hidden: prevColumn?.hidden };
      })
    );
  }, [
    serviceStatus,
    serviceStatusLoading,
    offeringProlongTypes,
    offeringProlongTypesLoading,
    hierarchyOfferList,
    prolongOfferListProps.selectedOffer,
    tableData,
  ]);

  const rowClassName = (record: IOfferParamsTableRecord) => {
    const isRootOffer = !record.parentId;
    const baseClassName = "product-params__tableRow";
    if (isRootOffer) return baseClassName;
    // if (record.isCurrentVersion) {
    //   return `${baseClassName} row_child row_current-version`;
    // }
    // if (record.isPastVersion) {
    //   return `${baseClassName} row_child row_past-version`;
    // }
    return `${baseClassName}`;
  };

  const selectOfferModalProps: ModalProps = useMemo(() => {
    const onCancel = () => {
      setActiveModal("");
      prolongOfferListProps.setSelectedOffer(undefined);
      prolongOfferListProps.onResetSearch();
    };
    const saveProplongOffer = () => {
      if (activeModalOptions?.key !== undefined) {
        onChangeTableData({
          key: activeModalOptions.key,
          fieldName: "prolongOffer",
          value: prolongOfferListProps.selectedOffer,
        });
        onChangeTableData({
          key: activeModalOptions.key,
          fieldName: "prolongTypeId",
          value: 2,
        });
      }
    };
    const onOk = () => {
      saveProplongOffer();
      onCancel();
    };

    return {
      visible: activeModal === "selectOfferForProlongation",
      width: "100%",
      okButtonProps: {
        onClick: onOk,
        disabled: !prolongOfferListProps.selectedOffer,
      },
      okText: "Выбрать предложение",
      cancelButtonProps: {
        style: {
          width: "108px",
        },
        onClick: onCancel,
      },
      onCancel,
    };
  }, [activeModal, prolongOfferListProps.selectedOffer]);

  const getFindLongWord = () => {
    let isShort = false;

    const longTitleColumns = columns.filter(({ key }) => key === "prolongOffer" || key === "id");

    if (longTitleColumns.find(({ hidden }) => !hidden)) {
      isShort = true;
    }
    return isShort;
  };

  return (
    <div className="product-params">
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns
          tableName="product-create-params-table"
          columns={columns}
          setColumns={setColumns}
        />
      </Row>
      <Row>
        <Col span={7}>
          <ProductParamsTree
            offer={offer}
            offerHierarchy={offerHierarchy}
            treeExpandedKeys={treeExpandedKeys}
            setTreeExpandedKeys={setTreeExpandedKeys}
            treeDataFormat={treeDataFormat}
            setTreeDataFormat={setTreeDataFormat}
            offerListSelectMode={offerListSelectMode}
          />
        </Col>
        <Col span={17}>
          <Table
            size="small"
            columns={columns.filter(({ hidden }) => !hidden)}
            dataSource={displayedTableData}
            pagination={false}
            rowClassName={rowClassName}
            className={getFindLongWord() ? "product-params-table-short" : "product-params-table"}
            scroll={{ x: "100%" }}
          />
        </Col>
      </Row>
      <Modal {...selectOfferModalProps}>
        <OfferList
          {...prolongOfferListProps}
          filterName="prolong-offer"
          disabledSearchFields={["isStandalone", "offeringStatusId"]}
          selectedAgreement={agreement}
        />
      </Modal>
    </div>
  );
};
